import React from "react";
import { UIForm, UIPageType } from "./allData";
import AddFormDialog from "./AddFormDialog";
import { Trash, CirclePlus, SquarePen, Type } from "lucide-react";
import { toast } from "@components/ui/use-toast";
import Confirmation from "@components/common/ConfirmDialog";
import {
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";
import { getFieldIconbyFieldType } from "./logicUtils";

type Props = {
  pageData: UIPageType;
  onSave: (data: UIPageType) => void;
  logicType?: any;
  definedVariables?: any;
  pluginId?: string;
  setSelectedBundle?: (bundleId: string) => void;
  setSelectedRequest?: (requestId: string) => void;
  selectedBundle?: string;
  selectedRequest?: string;
  meshVersion?: string;
  settingsWorkflows?: any[];
};

const SettingsUIPage: React.FC<Props> = ({
  pageData,
  onSave,
  logicType,
  definedVariables,
  pluginId,
  setSelectedBundle: setSelectedBundleProp,
  setSelectedRequest: setSelectedRequestProp,
  selectedBundle: selectedBundleProp,
  selectedRequest: selectedRequestProp,
  meshVersion,
  settingsWorkflows,
}) => {
  const { dispatch } = useMeshPluginContext();
  const [forms, setForms] = React.useState<UIForm[]>(pageData.forms || []);
  const [openFormDialog, setOpenFormDialog] = React.useState(false);
  const [editFormIndex, setEditFormIndex] = React.useState<number | null>(null);
  const [initialFormIds, setInitialFormIds] = React.useState<string[]>([]);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [formDeleteConfirmOpen, setFormDeleteConfirmOpen] =
    React.useState(false);
  const [formToDelete, setFormToDelete] = React.useState<number | null>(null);

  // Local state for bundle/request selection when not provided by parent
  const [localSelectedBundle, setLocalSelectedBundle] =
    React.useState<string>("");
  const [localSelectedRequest, setLocalSelectedRequest] =
    React.useState<string>("");

  // Use prop values if provided, otherwise use local state
  const selectedBundle = selectedBundleProp ?? localSelectedBundle;
  const selectedRequest = selectedRequestProp ?? localSelectedRequest;
  const setSelectedBundle = setSelectedBundleProp ?? setLocalSelectedBundle;
  const setSelectedRequest = setSelectedRequestProp ?? setLocalSelectedRequest;

  // Generate a unique ID for each form based on its content
  const generateFormId = (form: UIForm): string => {
    return JSON.stringify({
      title: form.formTitle,
      action: form.userAction,
      fields: form.fieldList,
    });
  };

  // Initialize form IDs on mount
  React.useEffect(() => {
    if (forms && forms.length > 0) {
      const ids = forms.map(generateFormId);
      setInitialFormIds(ids);
      setIsInitialized(true);
    } else {
      setInitialFormIds([]);
      setIsInitialized(true);
    }
  }, []);

  // Track unsaved forms by comparing form IDs
  React.useEffect(() => {
    // Don't check until initialized
    if (!isInitialized) {
      return;
    }

    if (initialFormIds.length === 0 && forms.length === 0) {
      dispatch({
        type: MeshPluginActions.SET_HAS_UNSAVED_FIELDS,
        payload: false,
      });
      return;
    }

    const currentFormIds = forms.map(generateFormId);

    // Check if arrays are different (length or content)
    const hasChanges =
      currentFormIds.length !== initialFormIds.length ||
      currentFormIds.some((id, index) => id !== initialFormIds[index]);

    dispatch({
      type: MeshPluginActions.SET_HAS_UNSAVED_FIELDS,
      payload: hasChanges,
    });
  }, [forms, initialFormIds, isInitialized]);

  // const addForm = (form: UIForm) => {
  //   const updatedForms = [...forms, form];
  //   setForms(updatedForms);

  //   // update parent
  //   onSave({
  //     pageTitle: settingsTitle,
  //     pageType: "settings",
  //     infoIcon: "info",
  //     description,
  //     forms: updatedForms,
  //   });
  // };

  // Improved addForm with validation
  const addForm = (form: UIForm) => {
    if (
      !form.formTitle?.trim() ||
      !form.fieldList ||
      form.fieldList.length === 0
    ) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Form title and at least one field are required.",
        duration: 3000,
      });
      return;
    }

    const updatedForms = [...forms, form];
    setForms(updatedForms);

    onSave({
      pageTitle: pageData.pageTitle,
      pageType: "settings",
      infoIcon: "info",
      description: pageData.description,
      forms: updatedForms,
      uid: pageData.uid ?? Date.now(),
    });
    toast({
      variant: "success",
      title: "Settings Added",
      description: "The form has been added successfully.",
      duration: 2000,
    });
  };

  const editForm = (updatedForm: UIForm) => {
    if (
      !updatedForm.formTitle?.trim() ||
      !updatedForm.fieldList ||
      updatedForm.fieldList.length === 0
    ) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Form title and at least one field are required.",
        duration: 3000,
      });
      return;
    }

    if (editFormIndex !== null) {
      const updatedForms = [...forms];
      updatedForms[editFormIndex] = updatedForm;

      setForms(updatedForms);

      onSave({
        pageTitle: pageData.pageTitle,
        pageType: "settings",
        infoIcon: "info",
        description: pageData.description,
        forms: updatedForms,
        uid: pageData?.uid ?? Date.now(),
      });

      setEditFormIndex(null);
      toast({
        variant: "success",
        title: "Settings Updated",
        description: "The form has been updated successfully.",
        duration: 2000,
      });
    }
  };

  // Delete form function - show confirmation
  const deleteForm = (index: number) => {
    setFormToDelete(index);
    setFormDeleteConfirmOpen(true);
  };

  // Confirm form deletion
  const confirmDeleteForm = () => {
    if (formToDelete !== null) {
      const updated = [...forms];
      updated.splice(formToDelete, 1);
      setForms(updated);

      // update parent
      onSave({
        pageTitle: pageData.pageTitle,
        pageType: "settings",
        infoIcon: "info",
        description: pageData.description,
        forms: updated,
        uid: pageData.uid ?? Date.now(),
      });

      toast({
        variant: "success",
        title: "Form Deleted",
        description: "The form has been deleted successfully.",
        duration: 2000,
      });

      setFormToDelete(null);
    }
    setFormDeleteConfirmOpen(false);
  };

  // Close form delete confirmation
  const closeFormDeleteConfirmation = () => {
    setFormDeleteConfirmOpen(false);
    setFormToDelete(null);
  };

  const handleAddFormClick = () => {
    // if (forms.length > 0 && forms[forms.length - 1].fieldList.length === 0) {
    //   toast({
    //     variant: "destructive",
    //     title: "Error",
    //     description:
    //       "Please add fields to the existing form before adding a new one.",
    //   });
    //   return;
    // }
    setOpenFormDialog(true);
  };

  return (
    <div>
      {formDeleteConfirmOpen && (
        <Confirmation
          text={
            "Are you sure you want to delete this form? All fields in this form will be deleted."
          }
          confirmAction={confirmDeleteForm}
          rejectAction={closeFormDeleteConfirmation}
          confirmText="Yes, delete it!"
          rejectText="Cancel"
          show={formDeleteConfirmOpen}
          closeDialog={closeFormDeleteConfirmation}
        />
      )}

      <div className="text-sm">
        {forms.length === 0 && (
          <div className="w-fit cursor-pointer mt-4 mb-1">
            <div
              onClick={handleAddFormClick}
              className="flex bg-primaryBlue text-background items-center gap-1 hover:text-muted-foreground py-2 px-4 rounded-full "
            >
              <CirclePlus size={15} /> Add Form
            </div>
          </div>
        )}

        {forms.length > 0 &&
          forms
            .filter((form) => form.fieldList.length > 0)
            .map((form, idx) => (
              <div key={idx} className="border px-2 py-2 rounded-lg bg-muted ">
                <div className="flex justify-between items-center mb-2 ">
                  <p className="font-semibold ml-1 text-muted-foreground text-sm">
                    {form.formTitle || `Form ${idx + 1}`}
                  </p>
                  <div className="flex">
                    <div
                      className="flex items-center justify-center p-1 bg-muted rounded-md"
                      title="Edit Form"
                    >
                      <SquarePen
                        size={13}
                        className="text-muted-foreground cursor-pointer hover:text-foreground"
                        onClick={() => {
                          setEditFormIndex(idx);
                          setOpenFormDialog(true);
                        }}
                      />
                    </div>
                    <div
                      className="flex items-center justify-center p-1 bg-muted rounded-md"
                      title="Delete Form"
                    >
                      <Trash
                        size={13}
                        className="text-muted-foreground cursor-pointer hover:text-foreground"
                        onClick={() => deleteForm(idx)}
                      />
                    </div>
                  </div>
                </div>

                <div className="text-sm grid md:grid-cols-2 gap-1 mb-1">
                  {/* Always render Title field first, non-editable */}
                  {form.fieldList.length > 0 &&
                    form.fieldList[0].givenName === "title" && (
                      <div className="p-2 text-xs border border-lightGray text-muted-foreground rounded-md bg-border font-mono flex items-center gap-2">
                        <Type size={13} />
                        <label
                          htmlFor={`field-${idx}-title`}
                          className="block text-xs font-medium text-muted-foreground"
                        >
                          {form.fieldList[0].fieldTitle || "Title"}
                        </label>
                      </div>
                    )}
                  {form.fieldList.slice(1).map((field, i) => {
                    const Icon = getFieldIconbyFieldType(
                      field?.fieldType || "",
                    );

                    return (
                      <div
                        key={i}
                        className="p-2 text-xs border border-lightGray text-muted-foreground rounded-md bg-border font-mono flex items-center gap-2"
                      >
                        <Icon size={13} />
                        <label
                          htmlFor={`field-${idx}-title`}
                          className="block text-xs font-medium text-muted-foreground"
                        >
                          {field.fieldType === "testConnectionBtn"
                            ? field.fieldTitle
                            : field.fieldTitle || ""}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

        {openFormDialog && (
          <AddFormDialog
            open={openFormDialog}
            handleClose={() => {
              setOpenFormDialog(false);
              setEditFormIndex(null);
            }}
            addForm={addForm}
            initialFormData={
              editFormIndex !== null ? forms[editFormIndex] : undefined
            }
            isEditing={editFormIndex !== null}
            editForm={editForm}
            definedVariables={definedVariables}
            logicType={logicType}
            pluginId={pluginId}
            setSelectedBundle={setSelectedBundle}
            setSelectedRequest={setSelectedRequest}
            selectedBundle={selectedBundle}
            selectedRequest={selectedRequest}
            meshVersion={meshVersion}
            settingsWorkflows={settingsWorkflows}
          />
        )}
      </div>
    </div>
  );
};

export default SettingsUIPage;
